import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, FileText, Loader2, MessageSquare, Send } from 'lucide-react'
import { INCLUDED_LICENSE_DAYS } from '@adthlete/common/utils/dealPricing'
import { cn } from '@/lib/utils'
import {
  useGetCollaborationMessagesQuery,
  useSendCollaborationMessageMutation,
  type Collaboration,
} from '@/redux/marketerApi'

const card = 'rounded-2xl border border-border bg-card p-4 shadow-sm'
const money = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
const day = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—'
const stamp = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="text-right font-medium text-foreground">{children}</dd>
  </div>
)

/** The agreed deal at a glance */
const DealDetails: React.FC<{ c: Collaboration }> = ({ c }) => {
  const b = (c.offerDetails as any)?.booking
  return (
    <section className={card}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Deliverable details</h3>
        <Link
          to="offer"
          relative="path"
          className="text-sm font-semibold text-[#3F7A0A] hover:underline"
        >
          View offer
        </Link>
      </div>
      <dl className="divide-y divide-border">
        {b?.kind === 'rate_card' ? (
          <>
            <Row label="Deliverable">{b.deliverableLabel}</Row>
            {b.theme ? <Row label="Content theme">{b.theme.label}</Row> : null}
            <Row label="Quantity">{b.quantity}</Row>
            <Row label="Rate">{money(b.unitPrice)}</Row>
          </>
        ) : b?.kind === 'custom' ? (
          <Row label="Custom request">{b.description}</Row>
        ) : (
          <Row label="Deliverables">
            {(c.structuredDeliverables ?? []).map((d) => `${d.platform} ${d.format}`).join(', ') ||
              '—'}
          </Row>
        )}
        <Row label={c.dealType === 'gifting' ? 'Pay' : 'Agreed total'}>
          {c.dealType === 'gifting' ? 'Product + bonuses' : money(c.paymentAmount)}
        </Row>
        <Row label="Due date">{day(c.contentDueDate)}</Row>
      </dl>
      <p className="mt-3 rounded-xl bg-[#F4FBEA] px-3 py-2 text-sm text-foreground">
        <span className="font-semibold">{INCLUDED_LICENSE_DAYS}-day usage rights included</span>
        <span className="block text-xs text-muted-foreground">Organic and paid, as agreed.</span>
      </p>
      {c.invoice ? (
        <p className="mt-3 flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">
            Invoice {c.invoice.invoiceNumber ?? ''} ·{' '}
            {c.invoice.paidAt ? 'Paid' : c.invoice.status}
          </span>
          {!c.invoice.paidAt && c.invoice.stripePaymentUrl ? (
            <a
              href={c.invoice.stripePaymentUrl}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-[#3F7A0A] hover:underline"
            >
              Pay
            </a>
          ) : null}
        </p>
      ) : null}
    </section>
  )
}

const Brief: React.FC<{ c: Collaboration }> = ({ c }) => {
  const [open, setOpen] = useState(false)
  const text = [c.campaign?.brief, c.campaign?.instructions].filter(Boolean).join('\n\n')
  if (!text) return null
  return (
    <section className={card}>
      <h3 className="mb-2 flex items-center gap-2 font-semibold text-foreground">
        <FileText className="h-4 w-4 text-[#16A34A]" /> Creative brief
      </h3>
      <p className={cn('text-sm text-foreground whitespace-pre-wrap', !open && 'line-clamp-5')}>
        {text}
      </p>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mt-2 text-sm font-semibold text-[#3F7A0A] hover:underline"
      >
        {open ? 'Show less' : 'Read the full brief'}
      </button>
    </section>
  )
}

/** The conversation with the athlete, inside the deal */
const Messages: React.FC<{ c: Collaboration; first: string }> = ({ c, first }) => {
  const [open, setOpen] = useState(true)
  const [text, setText] = useState('')
  const { data = [], refetch } = useGetCollaborationMessagesQuery(c.id)
  const [send, { isLoading }] = useSendCollaborationMessageMutation()
  const messages = data.filter((m) => m.messageType === 'user').slice(-8)
  const go = async () => {
    if (!text.trim()) return
    await send({ collaborationId: c.id, message: text.trim() }).unwrap()
    setText('')
    refetch()
  }
  return (
    <section className={card}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between font-semibold text-foreground"
      >
        <span className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-[#16A34A]" /> Messages
        </span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <>
          <ol className="mt-3 max-h-80 space-y-3 overflow-y-auto">
            {messages.length ? (
              messages.map((m) => (
                <li key={m.id} className="flex gap-2.5">
                  {m.author?.avatar ? (
                    <img
                      src={m.author.avatar}
                      alt=""
                      className="h-8 w-8 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
                      {(m.author?.name ?? '?').charAt(0)}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">
                        {m.author?.name ?? 'Athios'}
                      </span>{' '}
                      {stamp(m.createdAt)}
                    </p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{m.body}</p>
                  </div>
                </li>
              ))
            ) : (
              <li className="text-sm text-muted-foreground">
                No messages yet. Say hi to {first}.
              </li>
            )}
          </ol>
          <div className="mt-3 flex items-center gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && go()}
              placeholder="Write a message…"
              aria-label={`Message ${first}`}
              className="h-10 min-w-0 flex-1 rounded-lg border border-border bg-card px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-[#84CC16]"
            />
            <button
              type="button"
              onClick={go}
              disabled={isLoading || !text.trim()}
              aria-label="Send message"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#C0FD72] text-[#0F172A] disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
        </>
      ) : null}
    </section>
  )
}

/** What has happened on the deal, newest first */
const Activity: React.FC<{ c: Collaboration }> = ({ c }) => {
  const [all, setAll] = useState(false)
  const events = c.timeline
    .filter((t) => t.messageType !== 'user')
    .slice()
    .reverse()
  const shown = all ? events : events.slice(0, 6)
  return (
    <section className={card}>
      <h3 className="mb-3 font-semibold text-foreground">Activity</h3>
      <ol className="space-y-3">
        {shown.map((t, i) => (
          <li key={t.id} className="flex gap-3">
            <span
              className={cn(
                'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
                i === 0 ? 'bg-[#2563EB]' : 'bg-[#16A34A]',
              )}
            />
            <div className="min-w-0">
              <p className="text-sm text-foreground">{t.body}</p>
              <p className="text-xs text-muted-foreground">{stamp(t.createdAt)}</p>
            </div>
          </li>
        ))}
        {!events.length ? <li className="text-sm text-muted-foreground">Nothing yet.</li> : null}
      </ol>
      {events.length > 6 ? (
        <button
          type="button"
          onClick={() => setAll((a) => !a)}
          className="mt-3 text-sm font-semibold text-[#3F7A0A] hover:underline"
        >
          {all ? 'Show less' : `Show all ${events.length}`}
        </button>
      ) : null}
    </section>
  )
}

export const RightRail: React.FC<{ c: Collaboration; first: string }> = ({ c, first }) => (
  <aside className="space-y-4">
    <DealDetails c={c} />
    <Messages c={c} first={first} />
    <Brief c={c} />
    <Activity c={c} />
  </aside>
)
